import axios, { AxiosInstance } from 'axios';
import store from '@/store';
import { API_BASE_URL } from '@/utils/const';
import { captureApiError } from '@/utils/sentry';
import type { KodeAiPage, KodeAiTurn } from '@/utils/kode_ai';

/**
 * Kode AI, the in-app help assistant: one question to the API's
 * /kodeAi/ask, which answers it from what it knows about Screenkode
 * (components/kode_ai/Kode_AI_Widget asks; utils/kode_ai holds the rest).
 */

export interface KodeAiAskPayload {
  /** The typed question; null when a recording is sent instead. */
  message?: string | null;
  /** A recorded question for the API to transcribe: its MIME type and bytes, base64. */
  audio?: { mimeType: string; data: string } | null;
  /** The conversation so far, oldest first. */
  history: KodeAiTurn[];
  /** Where the user is in the app, so the answer can start there. */
  page: KodeAiPage | null;
}

export interface KodeAiAskData {
  answer: string;
  /** What the recording said, for a recorded question; null for a typed one. */
  transcript: string | null;
  model: string;
}

export interface KodeAiAskResult {
  ok: boolean;
  data: KodeAiAskData | null;
  /** What the API said when it refused or failed, to show in the panel. */
  error: string | null;
  status: number | null;
}

// Its own client rather than api/base: an answer can take a while, and what
// goes wrong (a busy model, too many questions in a row) is shown inside the
// panel rather than as the app's error toast.
const kodeApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-type': 'application/json',
  },
  timeout: 120 * 1000,
});

kodeApi.interceptors.request.use((request) => {
  const token = store.getters.sessionToken;
  if (token) request.headers.Authorization = `Bearer ${token}`;
  return request;
});

export async function askKodeAi(payload: KodeAiAskPayload): Promise<KodeAiAskResult> {
  try {
    const response = await kodeApi.post('/kodeAi/ask', payload);
    if (response && response.data && response.data.status && response.data.data) {
      return { ok: true, data: response.data.data as KodeAiAskData, error: null, status: response.status };
    }
    return { ok: false, data: null, error: (response && response.data && response.data.message) || null, status: response ? response.status : null };
  } catch (error: any) {
    captureApiError(error);
    const status: number | null = (error && error.response && error.response.status) || null;
    const message: string | null = (error && error.response && error.response.data && error.response.data.message) || null;
    return { ok: false, data: null, error: message, status };
  }
}
